/**
 * Result share link generation and parsing.
 *
 * Encoding: one calculated bend result is serialized to JSON, base64-url encoded,
 * and placed in the share route. The payload is self-contained; no server lookup
 * is required when another user opens it.
 *
 * In the iOS wrapper, `bend.509electric.com/share/*` can route into this same
 * receiver component through universal links.
 */

export interface SharedBend {
  calculatorId: string;
  result: Record<string, unknown>;
  setup?: SharedBendSetup;
  values?: Record<string, number>;
  bender?: SharedBenderSelection;
}

export interface SharedBendSetup {
  benderLabel?: string;
  conduitLabel?: string;
}

export interface SharedBenderSelection {
  benderId: string;
  conduitSize: string;
  material: 'EMT' | 'IMC' | 'RIGID' | 'ALUMINUM';
}

export type SharePayload = { kind: 'bend'; bend: SharedBend };

export interface ShareUrlOptions {
  origin?: string;
}

export function makeShareUrl(payload: SharePayload, options: ShareUrlOptions = {}): string {
  const json = JSON.stringify(payload);
  const b64 = base64UrlEncode(json);
  return `${shareBaseUrl(options.origin)}${b64}`;
}

export function parseShareToken(token: string): SharePayload | null {
  if (token.length > 2048) return null;
  try {
    const json = base64UrlDecode(token);
    const parsed = JSON.parse(json) as unknown;
    return isSharePayload(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function sharePathFromHash(hash: string): string | null {
  const match = hash.match(/^#\/share\/([^/?#]+)/);
  if (!match) return null;
  return `/share/${match[1]}`;
}

function isSharePayload(value: unknown): value is SharePayload {
  if (!value || typeof value !== 'object') return false;
  const maybe = value as { kind?: unknown; bend?: unknown };
  if (maybe.kind !== 'bend' || !maybe.bend || typeof maybe.bend !== 'object') return false;

  const bend = maybe.bend as {
    calculatorId?: unknown;
    result?: unknown;
    setup?: unknown;
    values?: unknown;
    bender?: unknown;
  };
  return (
    typeof bend.calculatorId === 'string' &&
    Boolean(bend.result) &&
    typeof bend.result === 'object' &&
    !Array.isArray(bend.result) &&
    isSharedBendSetup(bend.setup) &&
    isSharedBendValues(bend.values) &&
    isSharedBenderSelection(bend.bender)
  );
}

function isSharedBendSetup(value: unknown): value is SharedBendSetup | undefined {
  if (value === undefined) return true;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const setup = value as { benderLabel?: unknown; conduitLabel?: unknown };
  return (
    isOptionalNonEmptyString(setup.benderLabel) && isOptionalNonEmptyString(setup.conduitLabel)
  );
}

function isOptionalNonEmptyString(value: unknown): value is string | undefined {
  return value === undefined || (typeof value === 'string' && value.trim().length > 0);
}

function isSharedBendValues(value: unknown): value is Record<string, number> | undefined {
  if (value === undefined) return true;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  return Object.entries(value).every(
    ([key, entry]) => key.trim().length > 0 && typeof entry === 'number' && Number.isFinite(entry),
  );
}

function isSharedBenderSelection(value: unknown): value is SharedBenderSelection | undefined {
  if (value === undefined) return true;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const selection = value as {
    benderId?: unknown;
    conduitSize?: unknown;
    material?: unknown;
  };
  return (
    isNonEmptyString(selection.benderId) &&
    isNonEmptyString(selection.conduitSize) &&
    isSharedBenderMaterial(selection.material)
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isSharedBenderMaterial(value: unknown): value is SharedBenderSelection['material'] {
  return value === 'EMT' || value === 'IMC' || value === 'RIGID' || value === 'ALUMINUM';
}

function shareBaseUrl(origin?: string): string {
  const normalized = normalizeShareOrigin(origin ?? import.meta.env.VITE_PUBLIC_SHARE_ORIGIN);
  if (normalized) return `${normalized}/share/`;

  if (typeof window !== 'undefined') {
    const windowOrigin = normalizeShareOrigin(window.location.origin);
    if (windowOrigin) return `${windowOrigin}/share/`;
  }

  return '/share/';
}

function normalizeShareOrigin(origin: string | undefined): string {
  const trimmed = origin?.trim();
  if (!trimmed) return '';
  return trimmed.replace(/\/+$/, '');
}

function base64UrlEncode(s: string): string {
  if (typeof window === 'undefined') return Buffer.from(s, 'utf8').toString('base64url');
  const bytes = new TextEncoder().encode(s);
  const b64 = window.btoa(String.fromCharCode(...bytes));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  if (typeof window === 'undefined') return Buffer.from(b64, 'base64').toString('utf8');
  const raw = window.atob(b64);
  const bytes = Uint8Array.from(raw, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
